/**
 * Goal-progress step used by `CoCMFollowUpInteractionForm`. Renders one
 * card per active patient goal with date / GAS score / notes inputs, plus
 * the goal-creation section beneath. Pulled from the legacy
 * `Encounter.form.tsx`.
 *
 * @agentic connections
 */

import {
  Alert,
  Box,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useEffect, useMemo, useRef } from "react";
import type { UseFormReturnType } from "@mantine/form";
import type { Goal } from "@medplum/fhirtypes";

import { GAS_SCORE_SELECT_OPTIONS } from "@benchmark/core/constants";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { GoalCreationSection } from "@/components/shared/forms/GoalCreationSection";
import { EncounterGoalCard } from "./EncounterGoalCard";
import { usePatientGoals } from "@/hooks/domain/usePatientGoals";
import {
  dateOnlyToISO,
  localCalendarDateTodayISO,
  normalizeDateInputValue,
} from "@benchmark/ui/utils";
import type {
  CoCMFollowUpInteractionFormData,
  EncounterGoalProgressEntry,
} from "@benchmark/core/types";

const GAS_SCORE_OPTIONS = GAS_SCORE_SELECT_OPTIONS.map((option) => ({
  value: String(option.value),
  label: `${option.value >= 0 ? "+" : ""}${option.value} - ${option.label}`,
  description: option.description,
}));

/**
 * Props for {@link GoalProgressStep}.
 *
 * @agentic connections
 */
export type GoalProgressStepProps = {
  patientId: string | undefined;
  form: UseFormReturnType<CoCMFollowUpInteractionFormData>;
  disabled?: boolean;
};

/**
 * Active-goals progress + new-goal-creation step.
 *
 * @agentic connections
 */
export const GoalProgressStep = ({
  patientId,
  form,
  disabled = false,
}: GoalProgressStepProps) => {
  const {
    activeGoals,
    loading: goalsLoading,
    error: goalsError,
  } = usePatientGoals(patientId);
  const hydratedRef = useRef(false);

  const goalProgress = useMemo(
    () => form.values["goal-progress"] ?? [],
    [form.values],
  );

  useEffect(() => {
    if (hydratedRef.current) return;
    if (activeGoals.length === 0) return;

    const currentProgress = form.values["goal-progress"] ?? [];
    const needsUpdate = activeGoals.some((goal: Goal) => {
      if (!goal.id) return false;
      const existing = currentProgress.find((gp) => gp.goalId === goal.id);
      return !existing || !existing.effectiveDateTime;
    });

    hydratedRef.current = true;
    if (!needsUpdate) return;

    const updated: EncounterGoalProgressEntry[] = [...currentProgress];
    activeGoals.forEach((goal: Goal) => {
      if (!goal.id) return;
      const idx = updated.findIndex((gp) => gp.goalId === goal.id);
      if (idx === -1) {
        updated.push({
          goalId: goal.id,
          gasScore: 0,
          effectiveDateTime: dateOnlyToISO(localCalendarDateTodayISO()),
        });
      } else if (!updated[idx].effectiveDateTime) {
        updated[idx] = {
          ...updated[idx],
          effectiveDateTime: dateOnlyToISO(localCalendarDateTodayISO()),
        };
      }
    });

    form.setFieldValue("goal-progress", updated);
  }, [activeGoals, form]);

  const updateGoalProgress = (
    goalId: string,
    updates: Partial<{
      gasScore: number;
      effectiveDateTime: string;
      note?: string;
    }>,
  ) => {
    const current = form.values["goal-progress"] ?? [];
    const idx = current.findIndex((gp) => gp.goalId === goalId);
    const next: EncounterGoalProgressEntry[] =
      idx >= 0
        ? current.map((gp, i) => (i === idx ? { ...gp, ...updates } : gp))
        : [
            ...current,
            {
              goalId,
              gasScore: updates.gasScore ?? 0,
              effectiveDateTime: updates.effectiveDateTime ?? "",
              note: updates.note,
            },
          ];
    form.setFieldValue("goal-progress", next);
  };

  if (goalsLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={120} radius="md" />
        <Skeleton height={120} radius="md" />
      </Stack>
    );
  }

  if (goalsError) {
    return (
      <Alert color="red" title="Unable to load goals">
        {goalsError.message}
      </Alert>
    );
  }

  return (
    <Stack gap="xl">
      <Box>
        <FormSection title="Active Goals" showDivider={false}>
          {activeGoals.length === 0 ? (
            <Text c="dimmed" size="sm">
              No active goals found. Create new goals below.
            </Text>
          ) : (
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Record progress for each active goal using the Goal Attainment
                Scale (GAS). Progress updates are optional but recommended.
              </Text>
              {activeGoals.map((goal: Goal) => {
                if (!goal.id) return null;
                const progress = goalProgress.find(
                  (gp) => gp.goalId === goal.id,
                );
                const goalDescription = goal.description?.text || "Goal";
                const categoryDisplay =
                  goal.category?.[0]?.coding?.[0]?.display || "Uncategorized";
                const errorPrefix = `goal-progress.${goal.id}`;
                const dateError = form.errors[
                  `${errorPrefix}.effectiveDateTime`
                ] as string | undefined;
                const gasScoreError = form.errors[`${errorPrefix}.gasScore`] as
                  string | undefined;
                const hasError = !!(dateError || gasScoreError);

                return (
                  <EncounterGoalCard
                    key={goal.id}
                    title={goalDescription}
                    category={categoryDisplay}
                    hasError={hasError}
                    disabled={disabled}
                  >
                    <FormGroup grow={false} align="flex-start" wrap="wrap">
                      <Box style={{ flex: "0 1 200px" }}>
                        <FormField label="Date" required error={dateError}>
                          <TextInput
                            type="date"
                            disabled={disabled}
                            value={
                              progress?.effectiveDateTime
                                ? normalizeDateInputValue(
                                    progress.effectiveDateTime,
                                  )
                                : localCalendarDateTodayISO()
                            }
                            onChange={(event) => {
                              const dateValue = event.currentTarget.value;
                              if (
                                form.errors[`${errorPrefix}.effectiveDateTime`]
                              ) {
                                form.clearFieldError(
                                  `${errorPrefix}.effectiveDateTime`,
                                );
                              }
                              if (dateValue) {
                                updateGoalProgress(goal.id!, {
                                  effectiveDateTime: dateOnlyToISO(dateValue),
                                });
                              }
                            }}
                            error={!!dateError}
                            aria-label={`Progress date for ${goalDescription}`}
                          />
                        </FormField>
                      </Box>

                      <Box style={{ flex: "1 1 280px" }}>
                        <FormField
                          label="GAS Score"
                          required
                          error={gasScoreError}
                        >
                          <Select
                            placeholder="Select GAS score"
                            data={GAS_SCORE_OPTIONS}
                            value={
                              progress?.gasScore !== undefined
                                ? String(progress.gasScore)
                                : ""
                            }
                            onChange={(value) => {
                              if (form.errors[`${errorPrefix}.gasScore`]) {
                                form.clearFieldError(`${errorPrefix}.gasScore`);
                              }
                              if (value) {
                                updateGoalProgress(goal.id!, {
                                  gasScore: Number.parseInt(value, 10),
                                });
                              }
                            }}
                            searchable={false}
                            clearable={false}
                            error={!!gasScoreError}
                            disabled={disabled}
                            aria-label={`GAS score for ${goalDescription}`}
                          />
                        </FormField>
                      </Box>
                    </FormGroup>

                    <FormField
                      label="Notes"
                      description="Optional progress notes"
                    >
                      <Textarea
                        placeholder="Add any notes about this progress update..."
                        value={progress?.note || ""}
                        onChange={(event) => {
                          updateGoalProgress(goal.id!, {
                            note: event.currentTarget.value || undefined,
                          });
                        }}
                        minRows={2}
                        disabled={disabled}
                        aria-label={`Progress notes for ${goalDescription}`}
                      />
                    </FormField>
                  </EncounterGoalCard>
                );
              })}
            </Stack>
          )}
        </FormSection>
      </Box>

      <Box>
        <GoalCreationSection form={form} disabled={disabled} />
      </Box>
    </Stack>
  );
};
